import { getScoresByHintsUsed } from '@wt/app/game/_components/types';
import { getSiteMode, getSiteName, getSiteURL } from '@wt/utilities/siteMode';
import Link from 'next/link';
import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { HtmlTooltip } from '@wt/game/providers/tooltips';
import { browserName, isDesktop, osName } from 'react-device-detect';
import useWebShare from 'react-use-web-share';
import { useCopyToClipboard } from '@wt/shared/hooks/useCopyClipboard';
import { toast } from 'react-toastify';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

type HintUsage = 'None' | '1-2' | '3-5';

const ShareStatsButton = ({ shareText }: { shareText: string }) => {
  const [, copyShareText] = useCopyToClipboard();
  const { isSupported, share } = useWebShare();

  const shareStats = () => {
    // Firefox Android does not support navigator.share with text
    const isFirefoxAndroid = browserName === 'Firefox' && osName === 'Android';

    if (isSupported && !isFirefoxAndroid && !isDesktop) {
      share({
        title: `${getSiteName()} Stats`,
        text: shareText,
        url: getSiteURL(),
      });
    } else {
      copyShareText(shareText).then((c) => {
        toast(c ? 'Copied to clipboard' : 'Failed to copy to clipboard', {
          type: c ? 'success' : 'error',
        });
      });
    }
    return;
  };

  return (
    <button
      onClick={shareStats}
      className="btn btn-primary mt-2 h-[40px] min-h-[40px] w-fit shrink whitespace-nowrap rounded-lg text-base font-medium md:text-xl"
    >
      <span className="pointer-events-none whitespace-nowrap">
        <ShareOutlinedIcon className="pr-1" /> Share stats
      </span>
    </button>
  );
};

export default function EnhancedDualToggleHintChart() {
  const [displayMode, setDisplayMode] = useState<'best' | 'avg' | 'both'>(
    'both'
  );
  const [locationHints, setLocationHints] = useState<HintUsage>('None');
  const [yearHints, setYearHints] = useState<HintUsage>('None');
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [selectedHintCategory, setSelectedHintCategory] = useState('');
  const [selectedHintRange, setSelectedHintRange] = useState('');

  const scoresByHintsUsed = getScoresByHintsUsed();
  const hintData = {
    location: {
      None: { games: 0, best: 0, total: 0, avg: 0 },
      '1-2': { games: 0, best: 0, total: 0, avg: 0 },
      '3-5': { games: 0, best: 0, total: 0, avg: 0 },
    },
    year: {
      None: { games: 0, best: 0, total: 0, avg: 0 },
      '1-2': { games: 0, best: 0, total: 0, avg: 0 },
      '3-5': { games: 0, best: 0, total: 0, avg: 0 },
    },
  };

  if (!scoresByHintsUsed.totalCombined) return;

  // Populate hint data
  (['location', 'year'] as const).forEach((category) => {
    (['None', '1-2', '3-5'] as const).forEach((range) => {
      const source = scoresByHintsUsed[`${category}Combined`];
      hintData[category][range].games = 0;
      hintData[category][range].total = 0;
      hintData[category][range].best = 0;
      for (let i = 0; i < source.count.length; i++) {
        if (
          (i == 0 && range == 'None') ||
          (i > 0 && i <= 2 && range == '1-2') ||
          (i > 2 && i <= 5 && range == '3-5')
        ) {
          hintData[category][range].games += source.count[i];
          hintData[category][range].total += source.totals[i];
          hintData[category][range].avg =
            hintData[category][range].games > 0
              ? Math.round(
                  (10 * hintData[category][range].total) /
                    hintData[category][range].games
                ) / 10
              : 0;
          hintData[category][range].best = Math.max(
            source.max[i],
            hintData[category][range].best
          );
        }
      }
    });
  });

  // Check if user has data for a specific hint category and range
  const hasDataForHint = (category: 'year' | 'location', range: HintUsage) => {
    return hintData[category][range].games > 0;
  };

  // Get location scores based on location hint filter
  const getLocationScores = () => {
    return hintData.location[locationHints];
  };

  // Get year scores based on year hint filter
  const getYearScores = () => {
    return hintData.year[yearHints];
  };

  // Get data based on hint filter states
  const getFilteredData = () => {
    const locationScores = getLocationScores();
    const yearScores = getYearScores();

    // Calculate total by adding location and year scores
    const totalBest = locationScores.best + yearScores.best;
    const totalAvg = locationScores.avg + yearScores.avg;
    const totalGames = Math.min(locationScores.games, yearScores.games);

    return {
      location: locationScores,
      year: yearScores,
      total: { best: totalBest, avg: totalAvg, games: totalGames },
    };
  };

  // Handle location hint changes
  const handleLocationHintChange = (value: HintUsage) => {
    if (value !== 'None' && !hasDataForHint('location', value)) {
      setSelectedHintCategory('Location');
      setSelectedHintRange(value);
      setShowInfoPopup(true);
    } else {
      setLocationHints(value);
    }
  };

  // Handle year hint changes
  const handleYearHintChange = (value: HintUsage) => {
    if (value !== 'None' && !hasDataForHint('year', value)) {
      setSelectedHintCategory('Year');
      setSelectedHintRange(value);
      setShowInfoPopup(true);
    } else {
      setYearHints(value);
    }
  };

  const filteredData = getFilteredData();

  const barColors =
    getSiteMode() == 'whentaken'
      ? {
          best: '#8B5CF6',
          avg: '#B197FC',
        }
      : {
          best: '#ffc800',
          avg: '#ffd966',
        };

  // Transform data for the chart
  const data = [
    {
      category: 'Location',
      best: filteredData.location.best,
      avg: filteredData.location.avg,
      games: filteredData.location.games,
      fullMark: 500,
    },
    {
      category: 'Year',
      best: filteredData.year.best,
      avg: filteredData.year.avg,
      games: filteredData.year.games,
      fullMark: 500,
    },
    {
      category: 'Total',
      best: filteredData.total.best,
      avg: filteredData.total.avg,
      games: filteredData.total.games,
      fullMark: 1000,
    },
  ];

  // Helper function to get description of current filter state with games count
  const filterDescription = `Showing scores for: ${locationHints} Location Hints (${filteredData.location.games} games), ${yearHints} Year Hints (${filteredData.year.games} games)`;

  const CustomTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 p-3 rounded-md border-0 shadow-lg">
          <p className="font-medium text-white">{`${label}`}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name === 'best' ? 'Best Score: ' : 'Avg Score: '}
              {entry.value}
            </p>
          ))}
          <p className="text-gray-300 text-sm mt-1">
            Based on {data.games} {data.games === 1 ? 'game' : 'games'}
          </p>
        </div>
      );
    }
    return null;
  };

  const shareText = `📊 ${getSiteName().toUpperCase()} STATS 📊
Filters: Location Hints = ${locationHints}, Year Hints = ${yearHints}
🟪 Best  🔵 Average  ⚪ Remaining

📍 Location: (out of 500)
${generateEmojiBar(
  filteredData.location.best,
  500,
  '🟪',
  '⚪'
)} ${filteredData.location.best} ${getMedal(filteredData.location.best, 'best', 'location', yearHints, locationHints)}
${generateEmojiBar(
  filteredData.location.avg,
  500,
  '🔵',
  '⚪'
)} ${filteredData.location.avg} ${getMedal(filteredData.location.avg, 'avg', 'location', yearHints, locationHints)}

🗓️ Year: (out of 500)
${generateEmojiBar(
  filteredData.year.best,
  500,
  '🟪',
  '⚪'
)} ${filteredData.year.best} ${getMedal(filteredData.year.avg, 'best', 'year', yearHints, locationHints)}
${generateEmojiBar(
  filteredData.year.avg,
  500,
  '🔵',
  '⚪'
)} ${filteredData.year.avg} ${getMedal(filteredData.year.avg, 'avg', 'year', yearHints, locationHints)}

🏆 Total: (out of 1000)
${generateEmojiBar(
  filteredData.total.best,
  1000,
  '🟪',
  '⚪'
)} ${filteredData.total.best} ${getMedal(filteredData.total.avg, 'best', 'total', yearHints, locationHints)}
${generateEmojiBar(
  filteredData.total.avg,
  1000,
  '🔵',
  '⚪'
)} ${filteredData.total.avg} ${getMedal(filteredData.total.avg, 'avg', 'total', yearHints, locationHints)}

Based on ${filteredData.location.games} location games and ${filteredData.year.games} year games
Generated from ${getSiteURL()}`;

  return (
    <>
      <div className="w-full max-w-3xl bg-base-200 rounded-2xl text-white relative p-6">
        {/* Info popup for no data scenarios */}
        {showInfoPopup && (
          <div
            className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-10 backdrop-blur-sm rounded-lg"
            onClick={() => setShowInfoPopup(false)}
          >
            <div
              className="whentaken:bg-purple-800 bg-base-200 p-6 rounded-lg max-w-md text-center shadow-lg whentaken:border whentaken:border-purple-500 transform transition-all"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 whentaken:bg-purple-600 bg-white/10 rounded-full flex items-center justify-center text-white text-3xl animate-pulse">
                  💡
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2">No Data Available</h3>
              <p className="mb-4 whentaken:text-purple-200">
                {selectedHintRange === '1-2'
                  ? "You've never used a Hint in your games yet. Try using the 💡 icon during gameplay to see how Hints can improve your score!"
                  : `Become a power Hints user! Try using 3-5 ${selectedHintCategory} Hints in a game to unlock data for this section and see how it can significantly boost your performance.`}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  onClick={() => setShowInfoPopup(false)}
                  className="btn btn-primary px-4 py-2 rounded-md whentaken:border-0 whentaken:bg-purple-700 whentaken:hover:bg-purple-600 transition-colors"
                >
                  Dismiss
                </button>
                <Link
                  href="/#faq-what-are-hints-and-how-do-they-work"
                  className="btn btn-outline btn-accent px-4 py-2 rounded-md flex items-center justify-center whentaken:bg-purple-500 whentaken:text-white whentaken:hover:bg-purple-400 whentaken:border-0"
                >
                  <span>Find out more about Hints</span>💡
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-white">
            Performance Stats
          </h2>
          <Toggle
            values={['both', 'best', 'avg']}
            value={displayMode}
            setValue={setDisplayMode}
            label=""
            clazz="hidden md:flex"
          />
        </div>

        {/* Dual hint filters */}
        <div className="flex flex-col mb-4 space-y-2">
          <Toggle
            values={['both', 'best', 'avg']}
            value={displayMode}
            setValue={setDisplayMode}
            label="Score Type:"
            clazz="flex md:hidden"
          />
          <Toggle
            values={['None', '1-2', '3-5']}
            value={locationHints}
            setValue={handleLocationHintChange}
            label="Location Hints 📍:"
            clazz="flex"
          />
          <Toggle
            values={['None', '1-2', '3-5']}
            value={yearHints}
            setValue={handleYearHintChange}
            label="Year Hints 🗓️:"
            clazz="flex"
          />
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={data}
              margin={{ top: 5, right: 80, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#ffffff20"
                horizontal={false}
              />
              <XAxis
                type="number"
                domain={[
                  0,
                  (d: (typeof data)[number]) => {
                    // Dynamic domain based on category
                    const cat = data.find(
                      (item) => item.category === d.category
                    );
                    return cat ? cat.fullMark : 1000;
                  },
                ]}
                stroke="#fff"
                tick={{ fill: '#fff' }}
              />
              <YAxis
                dataKey="category"
                type="category"
                stroke="#fff"
                tick={{ fill: '#fff' }}
                width={80}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={{ paddingBottom: '10px', paddingTop: '5px' }}
                formatter={(value) => (value === 'best' ? 'Best' : 'Average')}
                iconType="circle"
              />
              {(displayMode === 'both' || displayMode === 'best') && (
                <Bar
                  dataKey="best"
                  fill={barColors.best}
                  name="best"
                  radius={[0, 4, 4, 0]}
                  animationDuration={600}
                  label={{
                    position: 'right',
                    fill: '#fff',
                    fontSize: 12,
                  }}
                />
              )}
              {(displayMode === 'both' || displayMode === 'avg') && (
                <Bar
                  dataKey="avg"
                  fill={barColors.avg}
                  name="avg"
                  radius={[0, 4, 4, 0]}
                  animationDuration={600}
                  label={{
                    position: 'right',
                    fill: '#fff',
                    fontSize: 12,
                  }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col gap-4 mt-4 text-sm text-gray-300 text-center whentaken:text-highlight">
          <p>{filterDescription}</p>
          <p>
            <HtmlTooltip
              arrow
              placement="top"
              title={
                'We recently changed the data we collect and launched a new visual. Click to learn more.'
              }
            >
              <Link
                href={`/#faq-why-do-my-stats-look-different`}
                className="hover:underline hover:decoration-dotted"
              >
                <HelpOutlineIcon className="size-4 mr-1" />
                Why do my stats look different?
              </Link>
            </HtmlTooltip>
          </p>

          <p>
            <Link
              href={`#legacy`}
              className="hover:underline hover:decoration-dotted"
            >
              Switch to classic stats view
            </Link>
          </p>
        </div>
      </div>
      <ShareStatsButton shareText={shareText} />
    </>
  );
}

const getMedal = (
  score: number,
  type: 'avg' | 'best',
  dataType: 'year' | 'location' | 'total',
  yearHints: HintUsage,
  locationHints: HintUsage
) => {
  // Define thresholds for medals based on criteria
  const thresholds = {
    year: {
      best: {
        None: { gold: 475, silver: 425 },
        '1-2': { gold: 485, silver: 445 },
        '3-5': { gold: 495, silver: 475 },
      },
      avg: {
        None: { gold: 450, silver: 400 },
        '1-2': { gold: 460, silver: 410 },
        '3-5': { gold: 475, silver: 425 },
      },
    },
    location: {
      best: {
        None: { gold: 475, silver: 425 },
        '1-2': { gold: 485, silver: 445 },
        '3-5': { gold: 495, silver: 475 },
      },
      avg: {
        None: { gold: 400, silver: 325 },
        '1-2': { gold: 425, silver: 350 },
        '3-5': { gold: 450, silver: 400 },
      },
    },
  };

  // For Total scores, we dynamically calculate as 95% of the sum of Year and Location thresholds
  if (dataType === 'total') {
    // Get thresholds for year and location with the same type (best/avg) and hints
    const yearThresholds = thresholds['year'][type][yearHints];
    const locationThresholds = thresholds['location'][type][locationHints];

    // Calculate total thresholds as 95% of the sum
    const goldThreshold = Math.round(
      (yearThresholds.gold + locationThresholds.gold) * 0.95
    );
    const silverThreshold = Math.round(
      (yearThresholds.silver + locationThresholds.silver) * 0.95
    );

    if (score >= goldThreshold) return '🥇';
    if (score >= silverThreshold) return '🥈';
    return '🥉';
  }

  // For Year and Location scores
  const criteriaThresholds =
    thresholds[dataType][type][dataType == 'year' ? yearHints : locationHints];

  if (score >= criteriaThresholds.gold) return '🥇';
  if (score >= criteriaThresholds.silver) return '🥈';
  return '🥉';
};

const generateEmojiBar = (
  value: number,
  maxPossible: number,
  filledEmoji: string,
  emptyEmoji: string
) => {
  const barMaxLength = 10;
  const filledLength = Math.round((value / maxPossible) * barMaxLength);
  const emptyLength = barMaxLength - filledLength;
  return filledEmoji.repeat(filledLength) + emptyEmoji.repeat(emptyLength);
};

function Toggle<T extends string>(props: {
  label: string;
  values: T[];
  value: string;
  setValue: (s: T) => void;
  clazz?: string;
}) {
  return (
    <div className={'items-center ' + (props.clazz ?? '')}>
      <span className="text-sm text-gray-300 w-32">{props.label}</span>

      <div className="bg-gray-800 rounded-full p-1 flex items-center">
        {props.values.map((v) => {
          return (
            <button
              onClick={() => props.setValue(v)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
                props.value === v
                  ? 'bg-accent text-white movies:text-black shadow-md'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
